import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, IconBadge, InfoCard, GradientButton, CTABanner } from '../../components/common/Primitives';
import ModuleCard from '../../components/common/ModuleCard';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import VideoLightbox from '../../components/common/VideoLightbox';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import { COACHING_OVERVIEW } from '../../lib/mockCoaching';
import { SP_OVERVIEW } from '../../lib/mockStudent';
import { SCHOOL_OVERVIEW } from '../../lib/mockSchool';
import { ArrowRight, PlayCircle } from 'lucide-react';

const TEAM_CODESIGN_IMAGE = 'https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/ugagjhsp_IMG-20260522-WA0004.jpg';

export default function CoachingOverview() {
  const d = COACHING_OVERVIEW;
  const [heroOpen, setHeroOpen] = useState(false);
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="coaching-overview-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={d.hero.ctaPrimary.to} data-testid="coaching-hero-adoption">{d.hero.ctaPrimary.label}</GradientButton>
                <GradientButton to={d.hero.ctaSecondary.to} variant="outline" data-testid="coaching-hero-request">{d.hero.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-3 shadow-soft overflow-hidden">
              <button
                type="button"
                onClick={() => setHeroOpen(true)}
                data-testid="coaching-hero-video-play"
                aria-label={d.hero.imageLabel}
                className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30"
              >
                <img
                  src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')}
                  alt={d.hero.imageLabel}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 shadow-soft group-hover:scale-105 transition-transform">
                    <PlayCircle className="h-8 w-8 text-navy" />
                  </span>
                </span>
              </button>
              <div className="px-3 pb-3 pt-4 text-center text-[11px] font-bold tracking-widest text-purple">{d.hero.imageLabel}</div>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-5">
            {d.facts.map((f, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                <div className="text-[10px] font-bold tracking-widest text-purple">{f.k}</div>
                <div className="mt-1 text-[13.5px] font-semibold text-navy">{f.v}</div>
              </div>
            ))}
          </div>

          {/* COMPLETE LEARNING ECOSYSTEM — 4 pillars (shared with Student profile) */}
          <div className="mt-6 rounded-3xl border border-canvas-200 bg-white p-6 md:p-7 shadow-soft" data-testid="coaching-learning-ecosystem">
            <div>
              <div className="text-[11.5px] font-bold tracking-widest text-purple">ONE COMPLETE LEARNING ECOSYSTEM</div>
              <div className="mt-1 text-[19px] md:text-[22px] font-extrabold text-navy leading-snug">VR Experiences · Web Learning Hub · AI Learning · Analytics</div>
              <p className="mt-1 max-w-2xl text-[13px] text-ink-500">Four connected layers work together to help every learner understand, practise, ask and progress.</p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { title: 'VR Experiences',   desc: 'Immersive Grade 8–9 modules for Science and Mathematics.',            color: 'purple', icon: 'Headset' },
                { title: 'Web Learning Hub', desc: 'Chapter videos, resources and unlimited quiz practice.',              color: 'royal',  icon: 'Monitor' },
                { title: 'AI Learning',      desc: 'Gyan Mitra answers questions verbally in English and Hindi.',        color: 'cyan',   icon: 'Bot' },
                { title: 'Analytics',        desc: 'Time spent, chapter progress and conceptual, applied and theoretical scores.', color: 'mint', icon: 'BarChart3' },
              ].map((p, i) => (
                <div key={i} className="relative rounded-2xl border border-canvas-200 bg-canvas-100 p-4 transition-transform hover:-translate-y-0.5 hover:shadow-soft">
                  <IconBadge icon={p.icon} color={p.color} />
                  <div className="mt-3 text-[13.5px] font-bold text-navy">{p.title}</div>
                  <p className="mt-1 text-[12px] leading-snug text-ink-500">{p.desc}</p>
                  {i < 3 && (
                    <span className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-canvas-200 text-purple">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT MAY FIT */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.fit.eyebrow} title={d.fit.title} desc={d.fit.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.fit.items.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={p.icon} color={p.color} size="lg" />
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING CONTENT */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.content.eyebrow} title={d.content.title} desc={d.content.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[...d.content.scienceRow, ...d.content.mathRow].map((m, i) => (
              <ModuleCard key={i} subject={m.subject} status="Available" title={m.title} desc={m.subject} duration="35–45 min" lang="EN / HI / AS" />
            ))}
          </div>
          <div className="mt-8 rounded-2xl bg-canvas-100 p-5 border border-canvas-200">
            <div className="text-[15px] font-bold text-navy">{d.content.exploreTitle}</div>
            <div className="mt-2 text-[13.5px] text-ink-700">{d.content.exploreList}</div>
            <div className="mt-2 text-[12px] italic text-ink-500">{d.content.exploreNote}</div>
          </div>
        </div>
      </section>

      {/* CONTINUE LEARNING BEYOND VR (shared with Student profile) */}
      <section className="px-6 py-16" data-testid="coaching-beyond-vr">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={SP_OVERVIEW.learningHubTeaser.eyebrow} title={SP_OVERVIEW.learningHubTeaser.title} desc={SP_OVERVIEW.learningHubTeaser.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_1fr]">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {SP_OVERVIEW.learningHubTeaser.items.map((c, i) => (
                <InfoCard key={i} icon={c.icon} color={c.color} title={c.title} desc={c.desc} />
              ))}
            </div>
            <div className="rounded-2xl bg-navy p-6 text-white shadow-card">
              <div className="text-[11.5px] font-bold tracking-widest text-cyan">DASHBOARD PREVIEW</div>
              <div className="mt-2 text-[18px] font-bold">{SP_OVERVIEW.learningHubTeaser.dashboard.title}</div>
              <p className="mt-1 text-[12.5px] text-white/70">{SP_OVERVIEW.learningHubTeaser.dashboard.desc}</p>
              <div className="mt-4 space-y-3">
                {SP_OVERVIEW.learningHubTeaser.dashboard.rows.map((r, i) => (
                  <div key={i} className="rounded-xl bg-white/5 p-3 border border-white/10">
                    <div className="text-[10.5px] font-bold tracking-widest text-cyan">{r.k}</div>
                    <div className="mt-1 text-[13.5px] font-semibold">{r.v}</div>
                  </div>
                ))}
              </div>
              <GradientButton to="/coaching/learning-hub" data-testid="coaching-open-learning-hub" className="mt-5 w-full justify-center">Open the Web Learning Hub</GradientButton>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT CAN BE USED */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.uses.eyebrow} title={d.uses.title} desc={d.uses.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.uses.items.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={p.icon} color={p.color} size="lg" />
                <h3 className="mt-4 text-[15px] font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADOPTION ESSENTIALS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.essentials.eyebrow} title={d.essentials.title} desc={d.essentials.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.essentials.items.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={p.icon} color={p.color} size="lg" />
                <h3 className="mt-4 text-[15px] font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIEW ALL VR EXPERIENCES — replaces the previous module portfolio section */}
      <section className="px-6 py-16" data-testid="coaching-view-all-cta">
        <div className="mx-auto max-w-[1240px] flex justify-center">
          <GradientButton to="/coaching/vr-experiences" data-testid="coaching-view-all-vr">
            View all VR experiences <ArrowRight className="h-4 w-4" />
          </GradientButton>
        </div>
      </section>

      {/* DEVELOPED AT IIT GUWAHATI (shared with School profile) */}
      <section className="px-6 py-16 bg-white" data-testid="coaching-developed-at">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_1fr] items-center">
            <div>
              <Eyebrow color="royal">{SCHOOL_OVERVIEW.developedAt.eyebrow}</Eyebrow>
              <h2 className="mt-2 text-[28px] md:text-[34px] font-extrabold leading-tight tracking-tight text-navy">{SCHOOL_OVERVIEW.developedAt.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-500">{SCHOOL_OVERVIEW.developedAt.desc}</p>
              <div className="mt-6 rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className="text-[15px] font-bold text-navy">{SCHOOL_OVERVIEW.developedAt.highlightTitle}</div>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-500">{SCHOOL_OVERVIEW.developedAt.highlightDesc}</p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={SCHOOL_OVERVIEW.developedAt.ctaPrimary.to}>{SCHOOL_OVERVIEW.developedAt.ctaPrimary.label}</GradientButton>
                <GradientButton to={SCHOOL_OVERVIEW.developedAt.ctaSecondary.to} variant="outline">{SCHOOL_OVERVIEW.developedAt.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-3 shadow-soft overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-black">
                <img
                  src={TEAM_CODESIGN_IMAGE}
                  alt={SCHOOL_OVERVIEW.developedAt.imageLabel}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  data-testid="coaching-developed-image"
                />
              </div>
              <div className="px-3 pb-3 pt-3 text-center text-[11px] font-bold tracking-widest text-royal">{SCHOOL_OVERVIEW.developedAt.imageLabel}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={d.cta.primary} />
        </div>
      </section>

      {heroOpen && (
        <VideoLightbox
          videoId={FEATURED_VIDEO_ID}
          title="Gyan Dhara — Coaching + Immersive Learning"
          onClose={() => setHeroOpen(false)}
        />
      )}
    </PageShell>
  );
}
